import { DatabaseIdGenerator } from '../../utility/database-id-generator.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import {
  BinaryBlob, DataModel, DataModelBuilder, IBinaryBlobConverter, SerializationContext, SerializedData,
  UnknownDataModelTypePolicy,
} from './data-model.js';
import { TrueTime } from '../../time/true-time.js';
import { TimeZone } from '../../time/timezone.js';

export class DataModelFactory {
  private static _instance: DataModelFactory | null = null;

  private constructor(
      private readonly _builders: Map<string, unknown>,
      private readonly _databaseIdGenerator: DatabaseIdGenerator,
      private readonly _unknownTypePolicy: UnknownDataModelTypePolicy,
  ) { }

  static get instance(): DataModelFactory | null {
    return this._instance;
  }

  get unknownTypePolicy(): UnknownDataModelTypePolicy {
    return this._unknownTypePolicy;
  }

  get databaseIdGenerator(): DatabaseIdGenerator {
    return this._databaseIdGenerator;
  }

  private getBuilder<T extends DataModel>(typeId: string): DataModelBuilder<T> | null {
    const builder = this._builders.get(typeId);

    return (builder ?? null) as DataModelBuilder<T> | null;
  }

  toData<T extends DataModel>(context: SerializationContext, model: T): SerializedData {
    const builder = this.getBuilder(model.$typeId);
    if(isNullOrUndefined(builder)) {
      throw new SerializationException(`Missing data model builder for type ID: ${model.$typeId}`);
    }

    builder.onBeforeSerialization(context, model);

    const data = model.toMap(context);
    data['$type_id'] = model.$typeId;
    data['$schema_version'] = model.$schemaVersion.encode();
    data['$last_update'] = TimeZone.encodeDate(TrueTime.now());

    const queryParams = builder.optimizeForQuery(context, model);
    if(isNotNullOrUndefined(queryParams)) {
      if(Object.keys(queryParams).length > 0) {
        data['$query'] = queryParams;
      }
    }

    return (builder as DataModelBuilder<T>).migrateFromCurrentSchemaVersion(context, model, data);
  }

  fromData<T extends DataModel>(context: SerializationContext, map: SerializedData): T {
    const typeId = map['$type_id'];
    if(isNullOrUndefined(typeId) || typeof typeId !== 'string' || typeId.length <= 0) {
      throw new SerializationException(`Data map does not contain a type ID.`, JSON.stringify(map));
    }

    const builder = this.getBuilder(typeId);
    if(isNullOrUndefined(builder)) {
      throw new SerializationException(`Missing data model builder for type ID: ${typeId}`);
    }

    builder.onBeforeDeserialization(context, map);

    const data = builder.migrateToCurrentSchemaVersion(context, map);
    if(isNullOrUndefined(data)) {
      throw new SerializationException(`Unknown schema version {${map['$schema_version']}} for ${typeId}`);
    }

    return (builder as DataModelBuilder<T>).build(context, data);
  }

  isSerializedData(map: SerializedData, typeId: string | null = null): boolean {
    const actualTypeId = map['$type_id'];

    if(isNullOrUndefined(actualTypeId) || typeof actualTypeId !== 'string' || actualTypeId.length <= 0) {
      return false;
    }

    const builder = this.getBuilder(actualTypeId);
    if(isNullOrUndefined(builder)) {
      return false;
    }

    if(isNotNullOrUndefined(typeId) && actualTypeId !== typeId) {
      return false;
    }

    return true;
  }

  static ensureInitialized(
      builders: Map<string, unknown>,
      databaseIdGenerator: DatabaseIdGenerator,
      unknownTypePolicy: UnknownDataModelTypePolicy
  ) {
    this._instance ??= new DataModelFactory(
        builders,
        databaseIdGenerator,
        unknownTypePolicy ?? UnknownDataModelTypePolicy.Ignore,
    );
  }
}

export class BinaryBlobFactory {
  private static _instance: BinaryBlobFactory | null = null;

  private constructor(
        private readonly converter: IBinaryBlobConverter,
  ) { }

  static get instance(): BinaryBlobFactory | null {
    return this._instance;
  }

  toBinaryBlob(bytes: Uint8Array): BinaryBlob {
    return this.converter.toBinaryBlob(bytes);
  }

  fromBinaryBlob(blob: BinaryBlob): Uint8Array {
    return this.converter.fromBinaryBlob(blob);
  }

  toBase64(bytes: Uint8Array): string {
    return this.converter.toBase64(bytes);
  }

  fromBase64(str: string): Uint8Array {
    return this.converter.fromBase64(str);
  }

  static ensureInitialized(converter: IBinaryBlobConverter) {
    this._instance ??= new BinaryBlobFactory(converter);
  }
}
